/**
 * shared/integrationsCatalog.ts
 *
 * UI-side MIRROR of backend/functions/src/integrations/catalog.ts — the provider
 * catalog SOT for the Control Center Integrations page. We can't import from
 * backend/functions into the React workspace (different tsconfig/bundle), so the
 * data + types are duplicated here, same as connectedAccounts.ts. The BACKEND file
 * is canonical; keep this byte-parallel. Wire Audit S10 flags the duplication.
 *
 * Purpose scoped to the follow-up consent-funnel boundary
 * (memory/project_followup_consent_funnel_boundary.md): a client-tool integration
 * is for sync/visibility/import + the fulfillment signal on Ava-funnel customers —
 * never a license to text the client's whole external base.
 */

export type IntegrationCategory =
  | 'scheduling-crm'
  | 'accounting'
  | 'marketing'
  | 'calendar'
  | 'inbox'
  | 'payments'
  | 'profile-social'

export type IntegrationAuthType = 'oauth' | 'api-key' | 'caldav' | 'manual'

/**
 * Which kind of business a client-tool is relevant to. Mirrors the backend.
 * The Control Center page shows the FULL catalog regardless of industry (a
 * client can connect anything they actually use) — this field only drives the
 * industry-branched ONBOARDING step, which lives backend-side. Kept here for
 * byte-parity with the canonical catalog (Wire Audit S10).
 */
export type IndustryCategory =
  | 'home-services'
  | 'accounting-tax'
  | 'insurance'
  | 'real-estate'

/** live = wired now · phase-b = UI stub, real connect in Phase B · request = "don't see yours". */
export type IntegrationStatus = 'live' | 'phase-b' | 'request'

export type IntegrationTier = 'ava-connection' | 'client-tool'

export interface IntegrationProvider {
  id: string
  name: string
  category: IntegrationCategory
  tier: IntegrationTier
  authType: IntegrationAuthType
  status: IntegrationStatus
  /**
   * Can a client START this connection today, end to end, and have it work?
   * Set from EVIDENCE (has a real client connected it, or is the path proven),
   * never from intent. `integrationsByTier` filters on it, so a false here keeps
   * the tool out of the Control Center — where every row carries a Connect button
   * and is therefore a promise. Onboarding still ASKS whether they use it, which
   * is a different question. Canonical copy + full rationale: backend
   * integrations/catalog.ts.
   */
  connectable: boolean
  purpose: string
  requiresSkill?: string
  requiresBooking?: boolean
  requiresInbox?: boolean
  /** Client-tools only. Absent = universal. Drives the onboarding industry branch. */
  industries?: readonly IndustryCategory[]
}

/** MIRROR of backend INTEGRATION_CATALOG — keep byte-parallel (backend is canonical). */
export const INTEGRATION_CATALOG: readonly IntegrationProvider[] = [
  // ── Ava-connections ──────────────────────────────────────────────────────────
  {
    id: 'google-calendar', name: 'Google Calendar', category: 'calendar',
    tier: 'ava-connection', authType: 'oauth', status: 'live', requiresBooking: true,
    connectable: true,
    purpose: 'So I can check your availability and put booked appointments on your calendar.',
  },
  {
    id: 'outlook-calendar', name: 'Outlook Calendar', category: 'calendar',
    tier: 'ava-connection', authType: 'oauth', status: 'phase-b', requiresBooking: true,
    connectable: true,
    purpose: 'So I can check your availability and put booked appointments on your calendar.',
  },
  {
    id: 'apple-calendar', name: 'Apple Calendar', category: 'calendar',
    tier: 'ava-connection', authType: 'caldav', status: 'phase-b', requiresBooking: true,
    connectable: true,
    purpose: 'So I can check your availability and put booked appointments on your calendar.',
  },
  {
    id: 'gmail', name: 'Gmail', category: 'inbox',
    tier: 'ava-connection', authType: 'oauth', status: 'live', requiresInbox: true,
    connectable: true,
    purpose: 'So I can triage your inbox and draft replies for you.',
  },
  {
    // 🔴 ADDED 2026-09-19. It was MISSING while a live client had it connected:
    // Austin's Outlook Mail showed only in the Settings "signed in as" list, and
    // the catalog's sole inbox option was Gmail — so any client who had not yet
    // connected an inbox was offered Gmail and nothing else, though we plainly
    // support Outlook. Found while merging the two integration surfaces: the
    // duplicate list could not be removed without losing a provider entirely.
    id: 'outlook-mail', name: 'Outlook Mail', category: 'inbox',
    tier: 'ava-connection', authType: 'oauth', status: 'live', requiresInbox: true,
    connectable: true,
    purpose: "So I can triage your inbox and draft replies for you.",
  },
  {
    id: 'stripe', name: 'Stripe', category: 'payments',
    tier: 'ava-connection', authType: 'oauth', status: 'live',
    connectable: true,
    purpose: 'So I can send invoices and collect payment with a pay link.',
  },
  {
    id: 'google-business-profile', name: 'Google Business Profile', category: 'profile-social',
    tier: 'ava-connection', authType: 'oauth', status: 'phase-b', requiresSkill: 'google',
    connectable: true,
    purpose: 'So I can post updates, reply to reviews, and keep your listing current.',
  },
  {
    id: 'facebook', name: 'Facebook', category: 'profile-social',
    tier: 'ava-connection', authType: 'oauth', status: 'phase-b', requiresSkill: 'social',
    connectable: true,
    purpose: 'So I can publish posts and manage your page.',
  },
  {
    id: 'instagram', name: 'Instagram', category: 'profile-social',
    tier: 'ava-connection', authType: 'oauth', status: 'phase-b', requiresSkill: 'social',
    connectable: true,
    purpose: 'So I can publish posts to your feed.',
  },
  {
    // 🔴 MISSING FROM THE CATALOG ENTIRELY UNTIL 2026-09-19 (John: "he chose the
    // ads skill… but what about the Google ads? I don't see that account
    // anywhere"). Google Ads connect shipped in August as a real per-client
    // OAuth, and the ROADMAP has been asking Austin for it as an amber row — but
    // the Integrations page, which is where a client goes to connect things,
    // could not show it because it was not in the list the page renders from.
    // Two surfaces, one of them blind.
    id: 'google-ads', name: 'Google Ads', category: 'profile-social',
    tier: 'ava-connection', authType: 'oauth', status: 'live', requiresSkill: 'ads',
    connectable: true,
    purpose: "So I can run and manage your search ads, and keep the wasted spend out.",
  },

  // ── Client-tools ─────────────────────────────────────────────────────────────
  {
    id: 'jobber', name: 'Jobber', category: 'scheduling-crm',
    tier: 'client-tool', authType: 'oauth', status: 'phase-b', industries: ['home-services'],
    connectable: false,
    purpose: "I'll sync the appointments I book into Jobber, and when you close a job there I can follow up with that customer.",
  },
  {
    id: 'housecall-pro', name: 'Housecall Pro', category: 'scheduling-crm',
    tier: 'client-tool', authType: 'oauth', status: 'phase-b', industries: ['home-services'],
    connectable: false,
    purpose: "I'll sync the appointments I book into Housecall Pro, and when you close a job there I can follow up with that customer.",
  },
  {
    id: 'servicetitan', name: 'ServiceTitan', category: 'scheduling-crm',
    tier: 'client-tool', authType: 'oauth', status: 'phase-b', industries: ['home-services'],
    connectable: false,
    purpose: "I'll sync the appointments I book into ServiceTitan, and when you close a job there I can follow up with that customer.",
  },
  {
    id: 'follow-up-boss', name: 'Follow Up Boss', category: 'scheduling-crm',
    tier: 'client-tool', authType: 'oauth', status: 'phase-b', industries: ['real-estate'],
    connectable: false,
    purpose: "I'll sync the appointments I book into Follow Up Boss, and when a deal closes there I can follow up with that client.",
  },
  {
    id: 'quickbooks', name: 'QuickBooks', category: 'accounting',
    tier: 'client-tool', authType: 'oauth', status: 'phase-b',
    connectable: false,
    purpose: "So I can see when your customers' invoices are paid or overdue — I'll send payment reminders and review requests to the ones who came through me.",
  },
  {
    id: 'xero', name: 'Xero', category: 'accounting',
    tier: 'client-tool', authType: 'oauth', status: 'phase-b',
    connectable: false,
    purpose: "So I can see when your customers' invoices are paid or overdue — I'll send payment reminders and review requests to the ones who came through me.",
  },
  {
    id: 'mailchimp', name: 'Mailchimp', category: 'marketing',
    tier: 'client-tool', authType: 'oauth', status: 'phase-b',
    connectable: false,
    purpose: "I'll import your customer list to grow your opted-in texting list, and keep new leads in sync.",
  },
  {
    id: 'constant-contact', name: 'Constant Contact', category: 'marketing',
    tier: 'client-tool', authType: 'oauth', status: 'phase-b',
    connectable: false,
    purpose: "I'll import your customer list to grow your opted-in texting list, and keep new leads in sync.",
  },
]

export const INTEGRATION_CATEGORY_LABEL: Record<IntegrationCategory, string> = {
  'scheduling-crm': 'Scheduling & CRM',
  accounting: 'Accounting',
  marketing: 'Email & Marketing',
  calendar: 'Calendar',
  inbox: 'Email Inbox',
  payments: 'Payments',
  'profile-social': 'Profile & Social',
}

/** Display order of categories within each tier. */
export const INTEGRATION_CATEGORY_ORDER: IntegrationCategory[] = [
  'calendar', 'inbox', 'payments', 'profile-social', // ava-connections lead with these
  'scheduling-crm', 'accounting', 'marketing',        // client-tools
]

/** PURE — providers of a tier, grouped by category in display order. */
export function integrationsByTier(tier: IntegrationTier): { category: IntegrationCategory; label: string; providers: IntegrationProvider[] }[] {
  return INTEGRATION_CATEGORY_ORDER
    .map((category) => ({
      category,
      label: INTEGRATION_CATEGORY_LABEL[category],
      // ⚠️ CONNECTABLE ONLY. This powers the Control Center's Integrations page,
      // where every row carries a Connect button — so a row here is a PROMISE that
      // pressing it works. Onboarding does not use this function: it asks 'do you
      // USE this?' over the whole catalog, which is a different question and stays.
      providers: INTEGRATION_CATALOG.filter((p) => p.tier === tier && p.category === category && p.connectable),
    }))
    .filter((g) => g.providers.length > 0)
}

/**
 * The providers a client can actually start today — what the Control Center may
 * offer a Connect button for.
 *
 * ⚠️ DERIVED HERE, NOT FILTERED IN A COMPONENT. The CC parity gate flagged the
 * filter when it lived in the page, and it was right to: "only connectable tools
 * belong on this page" is a RULE, and a rule in a component is one refactor away
 * from being two rules.
 */
export const CONNECTABLE_INTEGRATIONS: readonly IntegrationProvider[] =
  INTEGRATION_CATALOG.filter((p) => p.connectable)
